/**
 * LeadStageSelect
 *
 * Reusable async-paginated dropdown for selecting lead stages, backed by the
 * V2 list endpoint (`/list-lead-stages-v2`). Mirrors the PracticeAreaSelectV2
 * pattern — wraps react-select AsyncPaginate with a React Query-cached page
 * loader and shared AsyncSelect styles.
 *
 * Data source: POST /list-lead-stages-v2 (via `fetchLeadStagesListV2`).
 * Caching:     React Query (via `queryClient.fetchQuery`) caches each
 *              page+search combination for 5 minutes. Reopening the dropdown
 *              is instant on a cache hit.
 *
 * Behaviour:
 *  - Single-select by default; pass `isMulti` to enable multi-select.
 *  - Searchable — passes `inputValue` to the API as `keyword`.
 *  - Archived stages are labelled "(Archived)".
 *  - Re-fetches when `lead_stage_status`, `sort_by`, `sort_order`, or the body
 *    filters change (via `cacheUniqs`).
 *
 * @param {Object|Array|null} props.value                    - Selected option(s): full row(s) or null
 * @param {Function}          props.onChange                  - (option | option[] | null, actionMeta) => void
 * @param {boolean}           [props.isMulti]                 - Enable multi-select (default: false)
 * @param {string}            [props.placeholder]
 * @param {boolean}           [props.isClearable]
 * @param {boolean}           [props.isDisabled]
 * @param {string}            [props.className]
 * @param {number}            [props.pageSize]                - Records per page (default: 20)
 * @param {string}            [props.lead_stage_status]       - 'ACTIVE' | 'ARCHIVED' | '' (default: 'ACTIVE')
 * @param {string}            [props.sort_by]                 - API sort_by (default: 'lead_stage_name')
 * @param {string}            [props.sort_order]              - 'asc' | 'desc' (default: 'asc')
 * @param {Array|null}        [props.practice_area]           - Optional body filter (array of practice area IDs)
 * @param {string}            [props.practiceAreaId]          - Convenience: single practice area ID, mapped to `practice_area: [id]`
 * @param {boolean}           [props.show_unassigned_pa_column] - Optional body filter
 * @param {Array}             [props.additionalOptions]       - Options prepended to the first unsearched page (e.g. "Any")
 * @param {Element|null}      [props.menuPortalTarget]        - Pass document.body inside dialogs
 * @param {string}            [props.menuPosition]            - 'absolute' | 'fixed'
 * @param {Object}            [props.styles]                  - react-select style overrides
 * @param {string}            [props.name]
 * @param {string}            [props.id]
 * @param {Function}          [props.onBlur]
 * @param {Function}          [props.onFocus]
 */

import React, { useMemo, useState } from 'react';
import { AsyncPaginate } from 'react-select-async-paginate';
import { Tooltip } from 'primereact/tooltip';
import { useQueryClient } from '@tanstack/react-query';
import { fetchLeadStagesListV2 } from 'hooks/queries/useLeadStagesListV2Query';
import { queryKeys } from 'hooks/config/queryClient';
import '../shared/AsyncSelect.scss';
import '../PracticeAreaSelect/PracticeAreaSelectV2.scss';
import './LeadStageSelect.scss';

const getLabel = (option) => {
  const name = option?.lead_stage_name || option?.label || '';
  return option?.status === 'ARCHIVED' ? `${name} (Archived)` : name;
};

const getValue = (option) => option?.pk || option?.sk || option?.lead_stage_id || option?.value || '';
const EMPTY_OPTIONS = [];

const BASE_STYLES = {
  control: (base) => ({
    ...base,
    minHeight: '38px',
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function OptionLabel({ tooltipText, children, data }) {
  const safeTooltipClass = `lead-stage-option-tooltip-${String(getValue(data) || tooltipText || 'option')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, 80)}`;
  return (
    <>
      <span
        className={`pa-select-v2-option__label ${safeTooltipClass}`}
        style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {children}
      </span>
      <Tooltip target={`.${safeTooltipClass}`} content={tooltipText} position="top" />
    </>
  );
}

function OptionWithTooltip({ innerRef, innerProps, isFocused, isSelected, isDisabled, children, data }) {
  const tooltipText = getLabel(data) || (typeof children === 'string' ? children : '');
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={[
        'async-select-custom__option',
        isFocused ? 'async-select-custom__option--is-focused' : '',
        isSelected ? 'async-select-custom__option--is-selected' : '',
        isDisabled ? 'async-select-custom__option--is-disabled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <OptionLabel tooltipText={tooltipText} data={data}>
        {children}
      </OptionLabel>
    </div>
  );
}

function CheckboxOption({ innerRef, innerProps, isFocused, isSelected, isDisabled, children, data }) {
  const tooltipText = getLabel(data) || (typeof children === 'string' ? children : '');
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={[
        'async-select-custom__option',
        isFocused ? 'async-select-custom__option--is-focused' : '',
        isSelected ? 'async-select-custom__option--is-selected' : '',
        isDisabled ? 'async-select-custom__option--is-disabled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={`pa-select-v2-option${isDisabled ? ' pa-select-v2-option--disabled' : ''}`}>
        <span className={`pa-select-v2-option__checkbox${isSelected ? ' pa-select-v2-option__checkbox--checked' : ''}`}>
          {isSelected && (
            <svg className="pa-select-v2-option__checkbox__tick" viewBox="0 0 10 10">
              <polyline points="1.5,5 4,7.5 8.5,2.5" />
            </svg>
          )}
        </span>
        <OptionLabel tooltipText={tooltipText} data={data}>
          {children}
        </OptionLabel>
      </div>
    </div>
  );
}

function LeadStageSelect({
  value = null,
  onChange,
  isMulti = false,
  placeholder = 'Select lead stage',
  isClearable = true,
  isDisabled = false,
  className = '',
  pageSize = 20,
  lead_stage_status = 'ACTIVE',
  sort_by = 'lead_stage_name',
  sort_order = 'asc',
  practice_area = null,
  practiceAreaId = '',
  show_unassigned_pa_column,
  additionalOptions = EMPTY_OPTIONS,
  menuPortalTarget = null,
  menuPosition = 'absolute',
  styles = {},
  name,
  id,
  onBlur,
  onFocus,
  ...rest
}) {
  const queryClient = useQueryClient();
  const [inputValue, setInputValue] = useState('');

  // Backward-compat: support a single `practiceAreaId` string by mapping it to the V2 `practice_area` array body filter.
  const effectivePracticeArea = useMemo(() => {
    if (Array.isArray(practice_area) && practice_area.length) return practice_area;
    if (practiceAreaId) return [practiceAreaId];
    return null;
  }, [practice_area, practiceAreaId]);

  const practiceAreaScopeKey = useMemo(
    () => (Array.isArray(effectivePracticeArea) ? [...effectivePracticeArea].sort().join(',') : ''),
    [effectivePracticeArea]
  );

  const loadOptions = useMemo(
    () => async (searchInput, _loadedOptions, additional) => {
      const page = (additional?.page ?? 0) + 1;
      const keyword = searchInput?.trim() ?? '';

      const result = await queryClient.fetchQuery({
        queryKey: queryKeys.leadStagesV2.dropdown(
          keyword,
          page,
          pageSize,
          lead_stage_status,
          sort_by,
          sort_order,
          practiceAreaScopeKey || null
        ),
        queryFn: () =>
          fetchLeadStagesListV2({
            excludeFromLoading: true,
            limit: pageSize,
            page,
            sort_by,
            sort_order,
            keyword,
            lead_stage_status,
            practice_area: effectivePracticeArea,
            show_unassigned_pa_column,
          }),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
      });

      const prependedOptions = page === 1 && !keyword && Array.isArray(additionalOptions) ? additionalOptions : [];

      return {
        options: [...prependedOptions, ...(result?.rows ?? [])],
        hasMore: Boolean(result?.hasNext),
        additional: { page },
      };
    },
    [
      queryClient,
      pageSize,
      lead_stage_status,
      sort_by,
      sort_order,
      effectivePracticeArea,
      practiceAreaScopeKey,
      show_unassigned_pa_column,
      additionalOptions,
    ]
  );

  const mergedStyles = useMemo(() => ({ ...BASE_STYLES, ...styles }), [styles]);

  const components = useMemo(() => ({ Option: isMulti ? CheckboxOption : OptionWithTooltip }), [isMulti]);

  const cacheUniqs = useMemo(
    () => [lead_stage_status, sort_by, sort_order, practiceAreaScopeKey, show_unassigned_pa_column, additionalOptions],
    [lead_stage_status, sort_by, sort_order, practiceAreaScopeKey, show_unassigned_pa_column, additionalOptions]
  );

  const handleChange = (option, actionMeta) => {
    if (actionMeta?.action === 'select-option' || actionMeta?.action === 'clear') {
      setInputValue('');
    }
    onChange?.(option, actionMeta);
  };

  const handleInputChange = (next, { action }) => {
    if (action === 'input-change') {
      setInputValue(next);
      return;
    }
    if (action === 'set-value' || action === 'clear' || action === 'menu-close') {
      setInputValue('');
    }
  };

  return (
    <>
      <AsyncPaginate
        value={value}
        loadOptions={loadOptions}
        onChange={handleChange}
        getOptionLabel={getLabel}
        getOptionValue={getValue}
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        hideSelectedOptions={false}
        placeholder={placeholder}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isSearchable
        components={components}
        className={`lead-stage-select ${className}`.trim()}
        classNamePrefix="async-select-custom"
        styles={mergedStyles}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        cacheUniqs={cacheUniqs}
        additional={{ page: 0 }}
        debounceTimeout={300}
        loadingMessage={() => 'Loading lead stages...'}
        noOptionsMessage={({ inputValue: q }) => (q ? `No lead stages found for "${q}"` : 'No lead stages available')}
        menuShouldScrollIntoView={false}
        closeMenuOnScroll={(e) => e.target === document}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPosition}
        name={name}
        id={id}
        onBlur={onBlur}
        onFocus={onFocus}
        {...rest}
      />
    </>
  );
}

export default LeadStageSelect;
